import { AlertTriangle, Minus, Plus } from 'lucide-react';

const MAX_SEATS = 200;

export interface SeatCountFieldProps {
  seats: number;
  onChange: (seats: number) => void;
}

/** A table's seat count: typed, or one at a time with − / +. */
export function SeatCountField({ seats, onChange }: SeatCountFieldProps) {
  const button =
    'px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-40';
  return (
    <div>
      <label className="block text-xs text-gray-500" htmlFor="table-seats-input">
        Seats
      </label>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, seats - 1))}
          disabled={seats <= 0}
          className={button}
          aria-label="One seat fewer"
          data-testid="seat-minus-btn"
        >
          <Minus size={12} />
        </button>
        <input
          id="table-seats-input"
          type="number"
          value={seats}
          onChange={(e) =>
            onChange(Math.min(MAX_SEATS, Math.max(0, parseInt(e.target.value) || 0)))
          }
          min={0}
          max={MAX_SEATS}
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          data-testid="table-seats-input"
        />
        <button
          type="button"
          onClick={() => onChange(Math.min(MAX_SEATS, seats + 1))}
          disabled={seats >= MAX_SEATS}
          className={button}
          aria-label="One seat more"
          data-testid="seat-plus-btn"
        >
          <Plus size={12} />
        </button>
      </div>
    </div>
  );
}

export interface SeatCapacityWarningProps {
  /** Seats the table is set to. */
  seats: number;
  /** How many chairs fit around it at its size without overlapping. */
  fits: number;
  /** Why the seats as set do not fit: chairs on top of one another, or run past a table's ends. */
  reason?: 'overlap' | 'pastEnds' | null;
  /** Sets the table's seats to what fits. */
  onFit: (seats: number) => void;
}

/**
 * Says when a table is set to more seats than fit around it. It only tells:
 * the seats stay as set (dropping them would drop guests from the plan) and
 * "Set to N" is the producer's call.
 */
export function SeatCapacityWarning({
  seats,
  fits,
  reason = 'overlap',
  onFit,
}: SeatCapacityWarningProps) {
  if (seats <= fits) return null;
  return (
    <div
      role="status"
      className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
      data-testid="seat-capacity-warning"
    >
      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
      <div>
        {fits === 0 ? (
          // Not even one chair fits: the answer is a bigger table or other
          // sides, never "set it to 0 seats".
          <p>
            No chair fits along this side at this size. Make the table bigger, or put its chairs on
            other sides.
          </p>
        ) : (
          <>
            <p>
              {seats} {seats === 1 ? 'seat' : 'seats'} won&apos;t fit at this size — about {fits}{' '}
              {fits === 1 ? 'does' : 'do'}.{' '}
              {reason === 'pastEnds'
                ? 'The chairs will run past the ends of the table.'
                : 'The chairs will overlap.'}
            </p>
            <button
              type="button"
              onClick={() => onFit(fits)}
              className="mt-1 font-medium underline hover:no-underline"
              data-testid="seat-capacity-fit-btn"
            >
              Set to {fits}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export interface ChairSeatPanelProps {
  /** The chair's seat number, from its `S<n>` label, if it has one. */
  seat: number | null;
  /** What the chair belongs to: "Table 2", or the object's label. */
  parentName: string;
  onRemove: () => void;
}

/** A selected chair: which seat it is, and taking that one seat away. */
export function ChairSeatPanel({ seat, parentName, onRemove }: ChairSeatPanelProps) {
  return (
    <div
      className="mb-3 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600"
      data-testid="chair-seat-info"
    >
      <p>
        {seat !== null ? `Seat ${seat} at ` : 'A seat at '}
        {parentName}
      </p>
      <button
        type="button"
        onClick={onRemove}
        className="mt-1.5 flex items-center gap-1 text-red-600 hover:text-red-700"
        data-testid="remove-seat-btn"
      >
        <Minus size={12} />
        Remove this seat
      </button>
      <p className="mt-1 text-gray-400">The other chairs stay where they are.</p>
    </div>
  );
}
